import { BaseSeratoFilter } from '@/pixi/filters/BaseSeratoFilter'
import { EffectState } from '@/pixi/filters/types'

interface OldCameraUniforms {
  isAudioReactive: boolean,
  audioIntensity: number,
  intensity: number
}

export class OldCameraFilter extends BaseSeratoFilter {
  
  get uniforms (): OldCameraUniforms {
    return super.uniforms as OldCameraUniforms
  }

  constructor (effectState: EffectState) {
    const shader = `
    #define NUMBEROFSAMPLES 6
    #define VHSRES vec2(320.0, 240.0)
    #define BLURRYNESS 3.0

    varying vec2 vTextureCoord;
    uniform sampler2D uSampler;
    uniform float timeElapsed;
    uniform float intensity;
    
    uniform bool isAudioReactive;
    uniform float audioIntensity;

    // Book of shaders, uniform distributed random number generator
    float random(vec2 st) {
      return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
    }

    vec3 rgb2yiq(vec3 rgb) {
      return mat3(0.2989, 0.5959, 0.2115, 0.5870, -0.2744, -0.5229, 0.1140, -0.3216, 0.3114) * rgb;
    }

    vec3 yiq2rgb(vec3 yiv) {
      return mat3(1.0001, 0.9559, 0.62113, 1.000, -0.2719, -0.6470, 0.9999, -1.1060, 1.7029) * yiv;
    }

    vec3 generateNoise(vec2 n) 
    {
      float nrnd0 = random(n + 0.07);
      float nrnd1 = random(n + 0.11);	
      float nrnd2 = random(n + 0.13);
      float nrnd3 = random(n + 0.17);
        
      return vec3((nrnd2+nrnd3) / 2.0, nrnd0, nrnd1);
    }

    vec3 getTexture(vec2 uv) {
      vec3 yiq = vec3(0.0);
      for (int i = 0; i < NUMBEROFSAMPLES; i++) {
        yiq += (
          rgb2yiq(texture2D(uSampler, uv - (vec2(float(i), 0.0) / VHSRES.x)
          * (vec2(float(NUMBEROFSAMPLES - i)) / 2.0)
          * (vec2(float(i)) / BLURRYNESS)
          / 1.1
          ).xyz * vec2(float(NUMBEROFSAMPLES - i)).xxx / (3.4 * float(NUMBEROFSAMPLES)))
        );
      }
      return yiq2rgb(yiq);
    }

    void main(void)
    {
      float intensityMultiplier = intensity + 0.1;
      if (isAudioReactive) {
        intensityMultiplier = (intensity + 0.5) * audioIntensity;
      }
      float time = timeElapsed * -1.0;
      vec2 uv = vTextureCoord;
      vec4 originalPixelColor = texture2D(uSampler, uv);
      vec3 tex = getTexture(uv);

      // ac beat
      tex *= 1.0 + smoothstep(0.2, 0.5, generateNoise(vec2(uv.y, time))) / 84.0;
    
      // saturate
      tex = clamp(tex, 0.0, 1.0);

      // yiq
      tex = rgb2yiq(tex);
      tex += vec3(0.0, -0.1, 0.1);
      tex = yiq2rgb(tex);
      
      // blend original image
      tex = mix(originalPixelColor.rgb, tex, intensityMultiplier);

      gl_FragColor = originalPixelColor.a == 0.0 ? vec4(0.0) : vec4(tex, originalPixelColor.a);
    }`

    const uniforms: OldCameraUniforms = {
      isAudioReactive: false,
      audioIntensity: 0,
      intensity: 0.5
    }

    super(
      effectState,
      undefined,
      shader,
      uniforms
    )
  }

  updateUniforms () {
    this.uniforms.intensity = this.effectState.intensity
    this.uniforms.audioIntensity = this.getAudioIntensityValue()
    this.uniforms.isAudioReactive = this.effectState.audioReactivityEnabled
  }
}
